<template>
  <div class="list">
    <app-list-search
      v-if="showSearch"
      :list-type=listType
      :fields=fields
      :allowAdd="allowAdd"
      :allowAdvancedSearch="allowAdvancedSearch"
      :searchModel="searchModel"
      @updateSearchModel="$emit('updateSearchModel', $event)"
      @onSearchSubmit="$emit('onSearchSubmit', $event)"
      @onListAddClick="$emit('onListAddClick', $event)"
    />

    <app-load-content
      v-if="records"
      :loading="isListBusy"
      :is-empty.sync="searchIsEmpty"
      class="relative min-h-80px"
    >
      <div class="table-wrapper no-top-pagination">
        <div v-if="records && records.length > 0 && Array.isArray(groupedData)" >
          <table :id="listType + '-list'"
                 class="list-table"
                 cellpadding="0" cellspacing="0">
            <thead v-if="showHeaders">
              <tr>
                <th v-for="(column, i) in displayColumns" :key="column.id + '-' + i"
                    :style="'width:' + column.width"
                    :class="{'clickable-record': sortableHeaders}"
                    v-on:click="onHeaderClick(column.value)">
                  {{column.label}}

                  <div class="sorting-arrows" v-if="sortableHeaders && pagination && pagination.order === column.value">
                    <span v-if="pagination.sort === 'asc'" class="fad fa-sort-up"></span>
                    <span v-if="pagination.sort === 'desc'" class="fad fa-sort-down"></span>
                  </div>
                </th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="record in groupedData" :key="record.id"
                  v-on:click="onRecordClick(record)"
                  :class="{active: record.id === activeRecord.id, 'clickable-record': isItemClickable}"
              >
                <td v-for="(column, i) in displayColumns" :key="column.id + '-' + i" v-html="displayValue(record[column.value], column.customRenderer)"></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-else-if="records && records.length > 0 && !Array.isArray(groupedData)" >
          <div v-for="(group, key) in groupedData" :key="`${group.id}-${key}`">
            <h5 class="table-title">{{key}}</h5>
            <table :id="listType + '-list'"
                   class="list-table"
                   cellpadding="0" cellspacing="0">
              <thead v-if="showHeaders">
                <tr>
                  <th v-for="(column, i) in displayColumns" :key="i"
                      :style="'width:' + column.width"
                      :class="{'clickable-record': sortableHeaders}"
                      v-on:click="onHeaderClick(column.value)">
                    {{column.label}}

                    <div class="sorting-arrows" v-if="sortableHeaders && pagination && pagination.order === column.value">
                      <span v-if="pagination.sort === 'asc'" class="fad fa-sort-up"></span>
                      <span v-if="pagination.sort === 'desc'" class="fad fa-sort-down"></span>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody v-if="group && group.length">
                <tr v-for="(record, index) in group" :key="`${group.id}-group-${index}`"
                    v-on:click="onRecordClick(record)"
                    :class="{active: record === activeRecord, 'clickable-record': isItemClickable}"
                >
                  <td v-for="(column, i) in displayColumns" :key="i" v-html="displayValue(record[column.value], column.customRenderer)"></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <app-pagination v-if="pagination"
                        position="bottom"
                        :pagination="pagination"
                        :orderByOptions="displayColumns.filter(column => column.label !== '')"
                        @onPaginationChange="onPaginationChange"
        />
      </div>
    </app-load-content>
  </div>
</template>

<script>
import _ from 'lodash';

import ListSearch from './list_search';
import LoadContent from '../load-content';
import Pagination from '../pagination';

export default {
  props: {
    listType: String,
    fields: Object,
    records: Array,
    recordsPagination: Object,
    displayColumns: Array,
    allowAdd: {
      type: Boolean,
      default: () => { return false }
    },
    allowAdvancedSearch: {
      type: Boolean,
      default: () => { return false }
    },
    isListBusy: {
      type: Boolean,
      default: false
    },
    searchModel: {
      type: Object,
      default: () => { return {} }
    },
    isItemClickable: {
      type: Boolean,
      default: () => { return false }
    },
    activeRecord: {
      type: Object,
      default: () => { return {} }
    },
    showHeaders: {
      type: Boolean,
      default: true
    },
    sortableHeaders: {
      type: Boolean,
      default: false
    },
    groupBy: {
      type: String,
      default: ''
    },
    showSearch: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    groupedData () {
      let data = this.records;

      if(this.groupBy !== '') {
        data = {};

        this.records.map(record => {
          let grouping = this.displayValue(record[this.groupBy]);
          if(!data[grouping]) {
            data[grouping] = [];
          }
          data[grouping].push(record);
        });
      }

      return data;
    },
    pagination () {
      // return this.records.data.pagination;
      return this.recordsPagination;
    },
    searchIsEmpty() {
      if (
        this.records &&
        this.records.length
      ) return false;

      return true;
    }
  },
  components: {
    'app-list-search': ListSearch,
    'app-load-content': LoadContent,
    'app-pagination': Pagination
  },
  methods: {
    displayValue(value, customRenderer) {
      if (_.isFunction(customRenderer)) {
        return customRenderer(value);
      }

      else if(_.isObject(value)) {
        return value.label || value.title || value.name || value.value;
      }

      return value || "<span class='no-content'>N/A</span>";
    },
    onRecordClick(record) {
      if(this.isItemClickable) {
        this.$emit('onRecordClick', record)
      }
    },
    onPaginationChange ({type, value}) {
      const newPagination = {...this.pagination, ...{[type]: value}};
      if(type === 'order' && this.pagination.order !== value) {
        newPagination.sort = 'asc';
      }
      if(type === 'perPage') {
        newPagination.currentPage = 1;
      }

      this.$emit('onSearchSubmit', newPagination);
    },
    onHeaderClick (orderBy) {
      if(!this.sortableHeaders)
        return;

      if(this.pagination.order === orderBy) {
        this.onPaginationChange({type:'sort', value:this.pagination.sort === 'asc' ? 'desc' : 'asc'});
      }
      else {
        this.onPaginationChange({type:'order', value:orderBy});
      }
    }
  }
}
</script>

<style lang="scss">
@import "../../styles/components/list";
@import "../../styles/components/pagination";
.min-h-80px {
  min-height: 80px;
}
</style>